import { useCallback, useEffect, useState } from "react"

import {
  fetchDeployedBuildId,
  isStaleBuild,
  reloadOnce,
} from "@/lib/build-version"
import { env } from "@/lib/env"

type AppUpdate = {
  isUpdateAvailable: boolean
  applyUpdate: () => void
}

/**
 * Keeps a visitor who is already on the site from running a build that no
 * longer exists on the server. Polls the deployed build id, and treats a failed
 * chunk import as proof that the old build has been swept away.
 */
export function useAppUpdate(): AppUpdate {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

  const applyUpdate = useCallback(() => {
    reloadOnce()
  }, [])

  useEffect(() => {
    if (env.isDevelopment) {
      return
    }

    let isCancelled = false

    const check = async () => {
      if (document.visibilityState === "hidden") {
        return
      }

      const deployedBuildId = await fetchDeployedBuildId()

      if (!isCancelled && isStaleBuild(deployedBuildId)) {
        setIsUpdateAvailable(true)
      }
    }

    const interval = setInterval(check, env.updatePollInterval)

    window.addEventListener("focus", check)
    window.addEventListener("online", check)
    document.addEventListener("visibilitychange", check)

    void check()

    return () => {
      isCancelled = true
      clearInterval(interval)
      window.removeEventListener("focus", check)
      window.removeEventListener("online", check)
      document.removeEventListener("visibilitychange", check)
    }
  }, [])

  useEffect(() => {
    /**
     * Vite raises this when a lazily imported chunk cannot be fetched, which in
     * practice means the deploy this tab was built against is gone. There is
     * nothing to prompt about — the navigation has already failed.
     */
    const handlePreloadError = (event: Event) => {
      event.preventDefault()
      reloadOnce()
    }

    window.addEventListener("vite:preloadError", handlePreloadError)

    return () => {
      window.removeEventListener("vite:preloadError", handlePreloadError)
    }
  }, [])

  return { isUpdateAvailable, applyUpdate }
}
